import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChannelFormData } from '../types';
import { formatDateTime, timeAgo } from '../../../utils/stringUtils';

export interface IdentitiesTabProps {
  channel: any;
  formData: ChannelFormData;
  dashboardStats: any;
}

const IdentitiesTab: React.FC<IdentitiesTabProps> = ({
  channel,
  formData,
  dashboardStats,
}) => {
  const navigate = useNavigate();
  
  // Local state for collapsible sections
  const [mappedIdentityCollapsed, setMappedIdentityCollapsed] = useState(true);
  const [agentIdentitiesCollapsed, setAgentIdentitiesCollapsed] = useState(true);

  // Handler to navigate to identity details
  const showIdentityDetails = (identity: any) => {
    navigate(`/identities/${encodeURIComponent(identity.did)}`);
  };

  // Get identity badge based on status
  const getIdentityBadge = (identity: any) => {
    // First check if this is a local or remote identity
    if (identity.is_local !== undefined) {
      if (identity.is_local) {
        return <span className="badge badge-info">LOCAL</span>;
      } else {
        return (
          <>
            <span className="badge badge-warning">REMOTE</span>
            {identity.verified && (
              <span className="badge badge-success ms-1" style={{ marginLeft: '0.25rem' }}>VERIFIED</span>
            )}
          </>
        );
      }
    }
    
    // Fall back to usage-based badges
    const usageCount = identity.usage_count || identity.use_count || 0;
    const lastUsed = identity.last_used_at || identity.last_used;
    const createdAt = identity.created_at;
    
    if (!lastUsed && usageCount === 0) {
      return <span className="badge badge-secondary">Unused</span>;
    }
    
    if (lastUsed) {
      const lastUsedTime = new Date(lastUsed).getTime();
      const now = Date.now();
      const hoursSinceLastUse = (now - lastUsedTime) / (1000 * 60 * 60);
      
      if (hoursSinceLastUse <= 1) {
        return <span className="badge badge-success">Active</span>;
      }
    }
    
    if (createdAt) {
      const createdTime = new Date(createdAt).getTime();
      const now = Date.now();
      const hoursSinceCreation = (now - createdTime) / (1000 * 60 * 60);
      
      if (hoursSinceCreation <= 24) {
        return <span className="badge badge-info">New</span>;
      }
    }
    
    return null;
  };

  // Extract x-identity field names from schema (without payload data)
  const getIdentityFieldNames = useCallback((schema: any): string[] => {
    if (!schema) return [];
    
    const fieldNames: string[] = [];
    
    const traverse = (schemaObj: any, path: string[] = []) => {
      if (!schemaObj || typeof schemaObj !== 'object') return;
      
      // Check if this property has x-identity marker
      if (schemaObj['x-identity'] === true && path.length > 0) {
        fieldNames.push(path.join('.'));
      }
      
      // Traverse properties in schema
      if (schemaObj.properties) {
        Object.entries(schemaObj.properties).forEach(([propName, propSchema]) => {
          traverse(propSchema, [...path, propName]);
        });
      }
      
      // Traverse items in arrays
      if (schemaObj.items) {
        traverse(schemaObj.items, [...path, '[item]']);
      }
    };
    
    traverse(schema, []);
    return fieldNames;
  }, []);

  // Filter identities for this channel
  const channelIdentities = useMemo(() => {
    if (!dashboardStats?.identities || !formData.config_id) {
      return [];
    }

    const identityChannelStats = dashboardStats.metrics?.identity_channel_stats || [];
    
    // Get unique identity hashes for this channel (by config_id) from connection metrics
    const channelIdentityHashes = new Set(
      identityChannelStats
        .filter((stat: any) => stat.channel_config_id === formData.config_id)
        .map((stat: any) => stat.identity_hash)
    );

    // Filter identities to include:
    // 1. Identities that have been used with this channel (have connection metrics)
    // 2. Identities that were created by this channel (channel_config_id matches)
    return dashboardStats.identities.filter((identity: any) => 
      channelIdentityHashes.has(identity.identity_hash) || 
      channelIdentityHashes.has(identity.did) ||
      identity.channel_config_id === formData.config_id
    );
  }, [dashboardStats?.identities, dashboardStats?.metrics?.identity_channel_stats, formData.config_id]);

  // Calculate identity field count for the header badge
  const schema = formData.extension_rules?.json_schema;
  const identityFieldNames = getIdentityFieldNames(schema);
  const identityFieldCount = identityFieldNames.length;

  return (
    <>
      {/* Mapped Identity Fields */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-light" style={{ cursor: 'pointer' }} onClick={() => setMappedIdentityCollapsed(!mappedIdentityCollapsed)}>
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-id-badge"></i> Mapped Identity Fields
              {identityFieldCount > 0 && (
                <span className="badge badge-primary ml-2" style={{ verticalAlign: 'middle' }}>{identityFieldCount}</span>
              )}
            </h6>
            <i className={`fas fa-chevron-${mappedIdentityCollapsed ? 'down' : 'up'}`}></i>
          </div>
        </div>
        {!mappedIdentityCollapsed && (
        <div className="card-body">
          {identityFieldCount === 0 ? (
            <p className="text-muted">
              <i className="fas fa-exclamation-circle"></i> No x-identity fields are configured in the request extension schema for this channel. 
              Configure identity fields in the <strong>Request handling</strong> tab to enable identity mapping.
            </p>
          ) : (
            <>
              <p className="text-muted small mb-3">
                <i className="fas fa-info-circle"></i> These fields are marked with <code>x-identity</code> in the channel's request extension schema and are used to uniquely identify agents.
              </p>
              <div className="list-group">
                {identityFieldNames.map((fieldName) => (
                  <div key={fieldName} className="list-group-item d-flex justify-content-between align-items-center">
                    <span>
                      <i className="fas fa-key text-primary mr-2"></i>
                      <code className="text-dark">{fieldName}</code>
                    </span>
                    <span className="badge badge-primary badge-pill">x-identity</span>
                  </div>
                ))}
              </div>
              <div className="text-right mt-3">
                <small className="text-muted">
                  <i className="fas fa-lightbulb"></i> Total identity fields: <strong>{identityFieldCount}</strong>
                </small>
              </div>
            </>
          )}
        </div>
        )}
      </div>

      {/* Agent Identities for this Channel */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-light" style={{ cursor: 'pointer' }} onClick={() => setAgentIdentitiesCollapsed(!agentIdentitiesCollapsed)}>
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-users"></i> Agent Identities for this Channel
              {channelIdentities.length > 0 && (
                <span className="badge badge-primary ml-2" style={{ verticalAlign: 'middle' }}>{channelIdentities.length}</span>
              )}
            </h6>
            <i className={`fas fa-chevron-${agentIdentitiesCollapsed ? 'down' : 'up'}`}></i>
          </div>
        </div>
        {!agentIdentitiesCollapsed && (
        <div className="card-body">
          {/* Info box explaining identity counting */}
          <div className="alert alert-info mb-3" role="alert">
            <div className="d-flex align-items-start">
              <i className="fas fa-info-circle mt-1 mr-2"></i>
              <div>
                <strong>About Identity Tracking:</strong>
                <ul className="mb-0 mt-2 small">
                  <li>Identities shown here include those that were <strong>created by this channel</strong> or have <strong>made requests through this channel</strong></li>
                  <li>An identity appears if it was created here OR if it has connection metrics associated with this channel</li>
                  <li>The channel list count shows only identities with actual usage (connection metrics) for consistency across the dashboard</li>
                  <li>Newly created identities may appear here before they make their first request</li>
                </ul>
              </div>
            </div>
          </div>
          
          {channelIdentities.length > 0 ? (
            <div>
              {channelIdentities.map((identity: any) => (
                <div 
                  key={identity.did} 
                  className="card identity-card mb-3" 
                  style={{cursor: 'pointer'}} 
                  onClick={() => showIdentityDetails(identity)}
                >
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-8">
                        <h6 className="mb-1">
                          <i className="fas fa-fingerprint text-primary"></i>
                          <code className="small">{identity.did}</code>
                        </h6>

                        <small className="text-muted d-block">
                          <i className="fas fa-key"></i> Hash: {identity.identity_hash ? identity.identity_hash.substring(0, 6) + '...' + identity.identity_hash.slice(-6) : 'N/A'}
                        </small>
                        <small className="text-muted">
                          <i className="far fa-clock"></i> Created: {identity.created_at ? formatDateTime(identity.created_at, false) : 'N/A'}
                        </small>
                        {identity.last_used_at && (
                          <small className="text-muted d-block">
                            <i className="fas fa-history"></i> Last used: {formatDateTime(identity.last_used_at, true)} ({timeAgo(identity.last_used_at)})
                          </small>
                        )}
                      </div>
                      <div className="col-md-4 text-right">
                        {getIdentityBadge(identity)}
                        <div className="mt-2">
                          <small className="text-muted">
                            <i className="fas fa-redo-alt"></i> Used {identity.usage_count || identity.use_count || 0} {(identity.usage_count || identity.use_count || 0) === 1 ? 'time' : 'times'}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted">No identities have been created for this channel yet</p>
          )}
        </div>
        )}
      </div>
    </>
  );
};

export default IdentitiesTab;
